import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode, useState} from 'react';

import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {FONT_PROXIMA_TITLE, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Badge, {BadgeColor} from 'ui-library/Badge';
import CollapserChevron from 'ui-library/CollapserChevron';
import {GRAY_DARKER} from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

interface PlateContainerCollapserProps {
  clientStateId: string;
  setExpanded: (value: boolean) => void;
}

/** @package */
export const expandedGlobalClientState = makeUseGlobalClientState(true);

function PlateMapContainerCollapser({clientStateId, setExpanded}: PlateContainerCollapserProps): JSX.Element {
  const [globalExpanded, globalSetExpanded] = expandedGlobalClientState.useGlobalClientState(clientStateId);

  useOnMount(() => {
    setExpanded(globalExpanded);
  });

  return (
    <div
      className={classNames(
        css(styles.collapserChevronWrapper),
        'notebook-export--collapser-chevron-wrapper u-tiny-spacing-left'
      )}
    >
      <CollapserChevron
        isExpanded={globalExpanded}
        onToggleCollapsed={() => {
          globalSetExpanded(!globalExpanded);
          setExpanded(!globalExpanded);
        }}
        name="plate map"
      />
    </div>
  );
}

interface PlateContainerProps {
  /** A unique ID for the plate map; used for makeUseGlobalClientState. */
  clientStateId?: string;
  /** Text to be displayed in the header. */
  header: string;
  /** Controls to be displayed on the right side of the header, e.g. buttons. */
  headerControls: ReactNode;
  /** Callback for when the header is right-clicked. */
  onHeaderContextMenu: (e: ElementMouseEvent) => void;
  /** Subtext to be displayed in the header. */
  subheader: string;
  /** Type of Plate Map RECORD | DESIGN */
  plateMapType?: PlateMapType;
  /** Body of the container. */
  children?: ReactNode;
}

/**
 * A notebook-compatible "card" container, shared between PlateMap-rendering plugins
 * (i.e. plate designs and plate records).
 */
export default function PlateMapContainer(props: PlateContainerProps): JSX.Element {
  // This state is a duplicate of the globalClientState in PlateMapContainerCollapser.
  // useGlobalClientState requires a stable client id, and PlateMapContainer may be rendered before a plate
  // design is persisted, so we may not have a stable client id upon mounting this component. Hooks cannot be
  // used conditionally, but components can. So, we use useGlobalClientState in PlateMapContainerCollapser,
  // which is not rendered until we have a clientStateId, and that component mirrors the global state back
  // to this one on mount. While clientStateId is still nullish, we ignore this state and keep the component
  // expanded.
  const [expanded, setExpanded] = useState(false);

  // We use data-mediocre-item-select to only select the component when the header is clicked.
  // This overrides the default behavior of clicking anywhere that comes from SelectableNotePart.
  // TODO(BNCH-75467): Remove the duplicated styles which are used in temporary-aphrodite-styles.less
  return (
    <div
      data-mediocre-item-select={0}
      className={classNames(css(styles.root), 'notebook-export--plate-record')}
    >
      <div className={classNames(css(styles.headerWrapper), 'notebook-export--plate-record-header-wrapper')}>
        <div
          data-mediocre-item-select={1}
          className={classNames(css(styles.header), 'notebook-export--plate-record-header u-pointer')}
        >
          <div className={classNames(css(styles.headerTitle))} onContextMenu={props.onHeaderContextMenu}>
            <BenchlingIcon
              className={classNames(css(styles.headerIcon), 'notebook-export--plate-record-header-icon')}
              icon="plate"
            />
            <span className={classNames(css(styles.title), 'notebook-export--plate-record-title')}>
              {props.header}
            </span>
            <span
              data-test-component="PlateMapContainer"
              data-test-key="name"
              className={classNames(css(styles.name), 'notebook-export--plate-record-name')}
            >
              {props.subheader}
            </span>
            {props.plateMapType && (
              <Badge
                color={props.plateMapType === PlateMapType.RECORD ? BadgeColor.BLUE : BadgeColor.GRAY}
                bold={true}
              >
                {props.plateMapType === PlateMapType.RECORD ? 'RECORD' : 'DESIGN'}
              </Badge>
            )}
          </div>
          {(props.clientStateId == null || expanded) && props.headerControls}
        </div>
        {props.clientStateId != null && (
          <PlateMapContainerCollapser clientStateId={props.clientStateId} setExpanded={setExpanded} />
        )}
      </div>
      {(props.clientStateId == null || expanded) && (
        <div className={css(styles.contents)}>{props.children}</div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  contents: {
    padding: 16,
    display: 'flex',
    height: 625,
  },
  root: {
    borderRadius: 4,
    border: `1px solid ${Color.GRAY_300}`,
    margin: '8px 0',
    backgroundColor: Color.WHITE,
  },
  headerWrapper: {
    display: 'flex',
  },
  header: {
    padding: 8,
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${Color.GRAY_300}`,
    flex: '0 0 100%',
  },
  headerIcon: {
    verticalAlign: 'middle',
  },
  headerTitle: {
    flex: 1,
  },
  collapserChevronWrapper: {
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    ...FONT_PROXIMA_TITLE,
    marginLeft: 8,
    verticalAlign: 'middle',
  },
  name: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
    marginLeft: 8,
    marginRight: 8,
    verticalAlign: 'middle',
  },
});
